import { Dispatch, SetStateAction } from 'react';
import styled from 'styled-components';
import { TextButton } from 'ui/button';
import { AlertsEmptyState, AlertsZeroState, ErrorStatePageError } from 'ui/illustrations';
import {
  PlaceholderDescription,
  PlaceholderState,
  PlaceholderTitle,
} from 'ui/indexPages/PlaceholderState';
import { body2, colors, heading5 } from 'ui/theme';
import { RECENT_ALERTS_DURATION_WINDOW } from '../../constants';
import {
  CampaignAlert,
  useCampaignsTriggeredAlerts,
} from '../../hooks/useCampaignsTriggeredAlerts';
import { AlertHistories } from './AlertHistories';
import { AlertHistoryCard } from './AlertHistory.styled';
import { AlertHistorySkeleton } from './AlertHistorySkeleton';

const StyledPlaceholderState = styled(PlaceholderState)`
  & ${PlaceholderTitle} {
    ${heading5}
  }

  && ${PlaceholderDescription} {
    ${body2}
    color: ${colors.textSecondary};
  }
`;

const AlertHistoryErrorState = ({ refetch }: { refetch: () => void }) => {
  return (
    <AlertHistoryCard>
      <StyledPlaceholderState
        Illustration={ErrorStatePageError}
        title="We’ve hit a snag"
        data-test="alerts-error-state"
      >
        Sorry, we’re having trouble loading alerts for this campaign. Come back again later or{' '}
        <TextButton onClick={refetch}>try refreshing the page</TextButton>.
      </StyledPlaceholderState>
    </AlertHistoryCard>
  );
};

const AlertHistoryEmptyState = () => {
  return (
    <AlertHistoryCard>
      <StyledPlaceholderState
        Illustration={AlertsEmptyState}
        title="Alerts not enabled"
        data-test="alerts-empty-state"
      >
        Alerts aren’t enabled for this campaign. You can turn alerts on in the Delivery section for
        your campaign.
      </StyledPlaceholderState>
    </AlertHistoryCard>
  );
};

const AlertHistoryZeroState = () => {
  return (
    <AlertHistoryCard>
      <StyledPlaceholderState
        Illustration={AlertsZeroState}
        title="There’s nothing here"
        data-test="alerts-zero-state"
      >
        No alerts have been triggered for this campaign yet.
      </StyledPlaceholderState>
    </AlertHistoryCard>
  );
};

type AlertHistoryProps = {
  campaignId: number;
  campaignAlertsEnabled: boolean;
  selectedAlert: CampaignAlert | null;
  setSelectedAlert: Dispatch<SetStateAction<CampaignAlert | null>>;
};

export const AlertHistory = ({
  campaignId,
  campaignAlertsEnabled = false,
  selectedAlert,
  setSelectedAlert,
}: AlertHistoryProps) => {
  const { campaignTriggeredAlerts, hasError, isLoading, refetch } = useCampaignsTriggeredAlerts({
    variables: {
      campaignIds: [campaignId],
      alertTimeWindow: RECENT_ALERTS_DURATION_WINDOW,
    },
  });

  const { pastAlerts = [], recentAlerts = [] } = campaignTriggeredAlerts?.[0] || {};
  const hasTriggeredAlerts = pastAlerts?.length + recentAlerts?.length > 0;

  const refetchAlerts = () => refetch();

  switch (true) {
    case isLoading:
      return <AlertHistorySkeleton />;
    case hasError:
      return <AlertHistoryErrorState refetch={refetchAlerts} />;
    case !campaignAlertsEnabled && !hasTriggeredAlerts:
      return <AlertHistoryEmptyState />;
    case campaignAlertsEnabled && !hasTriggeredAlerts:
      return <AlertHistoryZeroState />;

    case hasTriggeredAlerts:
      return (
        <AlertHistories
          pastAlerts={pastAlerts}
          recentAlerts={recentAlerts}
          campaignId={campaignId}
          campaignAlertsEnabled={campaignAlertsEnabled}
          setSelectedAlert={setSelectedAlert}
          selectedAlert={selectedAlert}
        />
      );

    default:
      return <AlertHistoryErrorState refetch={refetchAlerts} />;
  }
};
